import { useFormik } from "formik";
import { isValid as isIBANValid } from "iban";
import { Divider } from "primereact/divider";
import { forwardRef, useImperativeHandle } from "react";
import { useFragment } from "react-relay";
import * as Yup from "yup";
import { SalutationDropdown, SalutationType } from "@components/salutation-dropdown";
import { useTranslation } from "@i18n/hooks";
import { CountryCode } from "@relay/accountEditBaseDataScreen_EditBusinessBaseDataMutation.graphql";
import { QUERY_FRAGMENT } from "./private-base-data-form.graphql";
import {
	ColSpan2,
	ColSpan4,
	InputGroupWrapper,
	ShortInputsRow,
	Wrapper,
} from "./private-base-data-form.styles";
import type {
	PrivateBaseDataRef,
	PrivateBaseDataProps,
	PrivateBaseDataFormState,
} from "./private-base-data-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { ResidenceDropdown } from "../../../../components/residence-dropdown";
import { ValidatedField } from "../../../../components/ValidatedField";

export const PrivateBaseDataForm = forwardRef<PrivateBaseDataRef, PrivateBaseDataProps>(
	function PrivateBaseDataForm({ baseDataFragmentRef, onSubmit }, ref) {
		const { t } = useTranslation(["common", "validation"]);
		const query = useFragment(QUERY_FRAGMENT, baseDataFragmentRef ?? null);

		const form = useFormik<PrivateBaseDataFormState>({
			initialValues: {
				salutation: query?.salutation ?? undefined,
				title: query?.title ?? "",
				firstName: query?.firstName ?? "",
				lastName: query?.lastName ?? "",
				street: query?.street ?? "",
				houseNumber: query?.houseNumber ?? "",
				postalCode: query?.postalCode ?? "",
				city: query?.city ?? "",
				countryCode: query?.countryCode ?? "DE",
				phoneNumber: query?.phoneNumber ?? "",
				iban: "" /* query?.accountData?.iban ?? "" */,
				bic: "" /* query?.accountData?.bic ?? */,
			},
			validateOnChange: false,
			validateOnBlur: false,
			validationSchema: Yup.object().shape({
				salutation: Yup.string().required(t("validation:required")),
				title: Yup.string().optional(),
				firstName: Yup.string().required(t("validation:required")),
				lastName: Yup.string().required(t("validation:required")),
				street: Yup.string().required(t("validation:required")),
				houseNumber: Yup.string().required(t("validation:required")),
				city: Yup.string().required(t("validation:required")),
				postalCode: Yup.string()
					.length(5, t("validation:postalCode"))
					.matches(/^[0-9]{5}/, t("validation:postalCode"))
					.required(t("validation:required")),
				countryCode: Yup.string().required(t("validation:required")),
				phoneNumber: Yup.string().optional(),
				iban: Yup.string()
					.test("test-card-number", t("validation:iban"), (value) =>
						isIBANValid(value ?? ""),
					)
					.required(t("validation:required")),
				bic: Yup.string(),
			}),
			onSubmit: (values) => {
				onSubmit?.(values);
			},
		});

		useImperativeHandle(ref, () => ({
			submit: form.submitForm,
			validate: () => form.validateForm().then((errors) => !errors),
		}));

		return (
			<Wrapper>
				<InputGroupWrapper>
					<ValidatedField<PrivateBaseDataFormState, SalutationType>
						formikConfig={form}
						name="salutation"
						label={t("common:accounts.salutation")}
						placeholder={t("common:placeholders.salutation")}
						component={SalutationDropdown}
					/>
					<ValidatedField<PrivateBaseDataFormState, string>
						formikConfig={form}
						name="title"
						label={t("common:accounts.title")}
						placeholder="..."
						component={DefaultTextFieldComponent}
					/>
					<ValidatedField<PrivateBaseDataFormState, string>
						formikConfig={form}
						name="firstName"
						label={t("common:accounts.firstName")}
						placeholder="..."
						component={DefaultTextFieldComponent}
					/>
					<ValidatedField<PrivateBaseDataFormState, string>
						formikConfig={form}
						name="lastName"
						label={t("common:accounts.lastName")}
						placeholder="..."
						component={DefaultTextFieldComponent}
					/>
					<ShortInputsRow>
						<ColSpan4>
							<ValidatedField<PrivateBaseDataFormState, string>
								formikConfig={form}
								name="street"
								label={t("common:accounts.street")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan4>
						<ColSpan2>
							<ValidatedField<PrivateBaseDataFormState, string>
								formikConfig={form}
								name="houseNumber"
								label={t("common:accounts.houseNumber")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan2>
					</ShortInputsRow>
					<ShortInputsRow>
						<ColSpan4>
							<ValidatedField<PrivateBaseDataFormState, string>
								formikConfig={form}
								name="city"
								label={t("common:accounts.city")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan4>
						<ColSpan2>
							<ValidatedField<PrivateBaseDataFormState, string>
								formikConfig={form}
								name="postalCode"
								label={t("common:accounts.postalCode")}
								placeholder="..."
								component={DefaultTextFieldComponent}
							/>
						</ColSpan2>
					</ShortInputsRow>
					<ValidatedField<PrivateBaseDataFormState, CountryCode>
						formikConfig={form}
						name="countryCode"
						label={t("common:accounts.country")}
						component={ResidenceDropdown}
					/>
					<ValidatedField<PrivateBaseDataFormState, string>
						formikConfig={form}
						name="phoneNumber"
						label={t("common:accounts.phoneNumber")}
						placeholder="..."
						component={DefaultTextFieldComponent}
					/>
				</InputGroupWrapper>
				<Divider />
				<h2 className="m-0">{t("common:accounts.bankData")}</h2>
				<InputGroupWrapper>
					<ValidatedField<PrivateBaseDataFormState, string>
						formikConfig={form}
						name="iban"
						label={t("common:accounts.iban")}
						placeholder={t("common:placeholders.iban")}
						component={DefaultTextFieldComponent}
					/>
					<ValidatedField<PrivateBaseDataFormState, string>
						formikConfig={form}
						name="bic"
						label={t("common:accounts.bic")}
						placeholder={t("common:placeholders.bic")}
						component={DefaultTextFieldComponent}
					/>
				</InputGroupWrapper>
			</Wrapper>
		);
	},
);
